/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import AuditDialog, {
  AuditDateFilterProps
} from '@ion/dls/src/editors/audit/AuditDialog';
import {AuditGridOptions} from '@ion/dls/src/editors/audit/AuditGrid';
import ViewAuditChangesDialog from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import {showAuditChangesDialog} from './state';
import {
  adminDevicesAuditIsEditingSelector,
  adminDevicesAuditShowChangesDialogSelector
} from './state-selectors';
import {useDeviceDetails} from './useDeviceDetails';

export interface AuditDeviceDialogProps extends AuditGridOptions, AuditDateFilterProps {
  enableAuditDialog?: boolean;
  enableGridMenuRows?: boolean;
};

/**
 * Holds dialog functionality for viewing audit information of a device.
 */
export const AuditDeviceDialog = ({
  enableGridMenuRows = false,
  showDifferences = false,
  showAuditVersion = false,
  enableRangeSelection = false,
  enableAuditDialog = false,
  enableDateFilter = false
}: AuditDeviceDialogProps) => {
  const dispatch         = useDispatch();
  const showAuditDevice  = useSelector(adminDevicesAuditIsEditingSelector);
  const showAuditChanges = useSelector(adminDevicesAuditShowChangesDialogSelector);

  const {
    auditItem,
    auditEntity,
    auditRecordData,
    showAuditAsset,
    showAuditAssetChanges,
    showAuditGroup,
    showAuditGroupChanges,
    onAuditClose
  } = useDeviceDetails();

  const toggleAuditDialog = (auditRecord = null, show: boolean = false) => (
    dispatch(showAuditChangesDialog(auditRecord, show) as any)
  );
  const openAuditDialog  = useCallback((auditRecord, show) => toggleAuditDialog(auditRecord, show), []);
  const closeAuditDialog = useCallback(() => toggleAuditDialog(), []);

  return (
    <>
      {(showAuditDevice || showAuditAsset || showAuditGroup) && enableAuditDialog && (
        <AuditDialog enableDateFilter={enableDateFilter}
          enableRangeSelection={enableRangeSelection}
          showDifferences={showDifferences}
          rowMenusEnabled={enableGridMenuRows}
          showAuditVersion={showAuditVersion}
          item={auditItem}
          entity={auditEntity}
          onCloseDialog={onAuditClose}
          onViewAuditChanges={openAuditDialog} />
      )}
      {(showAuditChanges || showAuditAssetChanges || showAuditGroupChanges) &&
      auditRecordData && enableAuditDialog && (
        <ViewAuditChangesDialog auditRecord={auditRecordData}
          onCloseDialog={closeAuditDialog}
          entity={auditEntity} />
      )}
    </>
  );
};

export default memo(AuditDeviceDialog);
